import Auth from '@aws-amplify/auth';
import { JS } from '@aws-amplify/core';
import { Alert, Button, Spinner, Input, PasswordInput, Span, LinkButton, theme } from '@salecycle/ui-component-library';
import { FC, useEffect, useState } from 'react';
import LoginFormHeader from './loginFormHeader';
import { AlertContainer, SubmitContainer, SubmitButtonContainer } from './styles';
import { useAppContext } from 'core/app/AppContext';
import { useTranslation } from 'react-i18next';

export interface IProps {
  emailAddress: string;
  changeState: (state: any, data?: any, emailAddress?: string) => void;
  successMessage: string;
  authData: any;
}

const CustomSignIn: FC<IProps> = props => {
  const { t } = useTranslation('login');
  const [email, setEmail] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [password, setPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [loginMessage, setLoginMessage] = useState('signIn.heading');
  const urlSearchParamNewUser = new URLSearchParams(window.location.search).get('newuser');
  const { setSignedIn } = useAppContext();

  useEffect(() => {
    setSignedIn(false);
    if (urlSearchParamNewUser) {
      setLoginMessage('welcome.heading');
    }
  }, [setSignedIn, setLoginMessage, urlSearchParamNewUser]);

  const handleSignIn = async event => {
    event.preventDefault();
    setIsLoading(true);
    const emailAddress = email.length ? email.toLowerCase() : props.emailAddress.toLowerCase();

    try {
      const user = await Auth.signIn(emailAddress, password.trim());
      if (user.challengeName === 'SOFTWARE_TOKEN_MFA') {
        props.changeState('confirmSignIn', user);
      } else if (user.challengeName === 'NEW_PASSWORD_REQUIRED') {
        props.changeState('requireNewPassword', user);
      } else {
        checkContact(user);
      }
      window.history.pushState({}, document.title, window.location.pathname);
    } catch (error) {
      const err: any = error;
      // Temporary error message changes (until I18n is added to the Auth screens)
      let errorText = '';
      if (err.code === 'UserNotConfirmedException') {
        errorText = err.message;
      } else if (err.code === 'PasswordResetRequiredException') {
        errorText = err.message;
      } else if (err.code === 'NotAuthorizedException' || err.code === 'UserNotFoundException') {
        errorText = t('errors.notAuthorizedException');
      } else if (err.code === 'UnexpectedLambdaException') {
        errorText = t('errors.unexpectedLambdaException');
      } else {
        errorText = t('errors.unexpectedLambdaException');
      }
      setEmail('');
      setErrorMessage(errorText);
      setPassword('');
    }

    setIsLoading(false);
  };

  const checkContact = (user: any) => {
    if (!Auth || typeof Auth.verifiedContact !== 'function') {
      throw new Error('No Auth module found, please ensure @aws-amplify/auth is imported');
    }
    Auth.verifiedContact(user).then(data => {
      if (!JS.isEmpty(data.verified)) {
        props.changeState('signedIn', user, '');
      } else {
        user = Object.assign(user, data);
        props.changeState('verifyContact', user);
      }
    });
  };

  const handleStateChange = (stateChange: string) => {
    props.changeState(stateChange);
  };

  return (
    <>
      <LoginFormHeader color={theme.designToken.newPrimary} heading={t(loginMessage)} />

      {(props.successMessage || errorMessage) && (
        <AlertContainer>
          {props.successMessage && <Alert heading={props.successMessage} variation="success" />}
          {errorMessage && <Alert heading={errorMessage} variation="error" />}
        </AlertContainer>
      )}

      <form onSubmit={handleSignIn} data-testid="LoginForm">
        <Input
          ariaLabel={t('emailInput.label')}
          label={t('emailInput.label')}
          color={theme.designToken.newPrimary}
          inputId="EmailAddressInput"
          type="email"
          inputTestId="EmailInput"
          required={true}
          value={email.length ? email : props.emailAddress}
          onChange={e => setEmail(e)}
          key="email"
          placeholder={t('emailInput.placeholder')}
        />

        <PasswordInput
          ariaLabel={t('passwordInput.label')}
          label={t('passwordInput.label')}
          color={theme.designToken.newPrimary}
          required={true}
          value={password}
          onChange={e => setPassword(e)}
          key="password"
          placeholder={t('passwordInput.placeholder')}
        />

        <SubmitContainer>
          <SubmitButtonContainer>
            <Button type="submit" variation="newPrimary" onClick={handleSignIn} disabled={isLoading}>
              {t('signInButton')}
            </Button>

            {isLoading && <Spinner />}
          </SubmitButtonContainer>
        </SubmitContainer>

        <SubmitContainer>
          <Span color={theme.designToken.newPrimary}>{t('forgotPasswordText')}</Span>
          <LinkButton
            color={theme.designToken.newPrimary}
            hoverColor={theme.designToken.newSecondary}
            onClick={() => handleStateChange('forgotPassword')}
          >
            {t('forgotPasswordLink')}
          </LinkButton>
        </SubmitContainer>
        <SubmitContainer>
          <LinkButton
            color={theme.designToken.newPrimary}
            hoverColor={theme.designToken.newSecondary}
            onClick={() => handleStateChange('termsAndConditions')}
          >
            {t('termsAndConditionsLink')}
          </LinkButton>
        </SubmitContainer>
      </form>
    </>
  );
};

export default CustomSignIn;
